/* =============================================================================
   HK catalog core — shared by the header drawer (hk-catalog-drawer.js) and the
   hero section (hk-catalog-slideshow.js).

   What lives here:
   - product-card fetching with a two-level cache (memory + sessionStorage, shared
     by header and hero, survives page navigations for 15 minutes)
   - lazy creation of the third-column ("L3") panes — they are NOT in the HTML,
     they are built from the L2 link the first time they are needed
   - the menu controller: hover (with hover-intent), keyboard, touch (two-tap)
   Cards are rendered by Shopify, per locale, through alternate templates:
     /collections/<handle>?view=hk-cards   (templates/collection.hk-cards.liquid)
     /products/<handle>?view=hk-card       (templates/product.hk-card.liquid)
   ============================================================================= */
(function () {
  'use strict';
  if (window.HKCatalog) return;

  var shopify = window.Shopify || {};
  var ROOT = (shopify.routes && shopify.routes.root) || '/';
  var THEME = (shopify.theme && shopify.theme.id) || 0;

  var mqDesktop = window.matchMedia('(min-width: 990px)');
  var mqCoarse = window.matchMedia('(pointer: coarse)');

  var CARD_TTL = 15 * 60 * 1000;   /* how long a cached card list is trusted */
  var CARDS_PER_PAGE = 6;
  var INTENT_MS = 80;              /* hover-intent delay */
  var SS_PREFIX = 'hk:cards:v3:' + THEME + ':' + ROOT + ':';

  var I18N_DEFAULT = {
    viewProduct: 'Vezi produsul',
    viewAll: 'Vezi toate',
    noProducts: 'Nu există produse de afișat.',
    setCollection: 'Setează link-ul spre o colecție (/collections/...).',
    prev: 'Previous',
    next: 'Next'
  };

  /* ---------- small helpers ---------- */
  function isHoverDesktop() { return mqDesktop.matches && !mqCoarse.matches; }
  /* touch layout = phone/tablet, or a touch-first device at desktop width */
  function isTouchMode() { return !mqDesktop.matches || mqCoarse.matches; }

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function closest(el, sel) {
    return el && el.closest ? el.closest(sel) : null;
  }

  function readI18n(el) {
    var out = {};
    Object.keys(I18N_DEFAULT).forEach(function (k) { out[k] = I18N_DEFAULT[k]; });
    var host = closest(el, '[data-hk-i18n]');
    if (host) {
      try {
        var parsed = JSON.parse(host.getAttribute('data-hk-i18n'));
        Object.keys(parsed).forEach(function (k) { out[k] = parsed[k]; });
      } catch (e) { /* keep defaults */ }
    }
    return out;
  }

  /* ---------- link -> target (collection / product) ---------- */
  function extractTarget(url) {
    if (!url) return null;
    var path = String(url);
    try { path = new URL(path, window.location.origin).pathname; }
    catch (e) { path = path.split('?')[0]; }

    /* /collections/x/products/y is a product link: check products first */
    var prod = path.match(/\/products\/([^\/\?#]+)/);
    if (prod) return { type: 'product', handle: prod[1] };

    var col = path.match(/\/collections\/([^\/\?#]+)/);
    if (col) return { type: 'collection', handle: col[1] };

    return null;
  }

  function keyFor(t) { return t ? (t.type + ':' + t.handle) : ''; }

  function pickBestTargetUrl(preferredUrl, pane) {
    if (extractTarget(preferredUrl)) return preferredUrl;
    if (pane) {
      var first = pane.querySelector('[data-hk-l3-preview][href*="/collections/"], [data-hk-l3-preview][href*="/products/"]');
      if (first) return first.getAttribute('href');
    }
    return preferredUrl || null;
  }

  /* ---------- card cache: memory -> sessionStorage -> network ---------- */
  var memCache = new Map();   /* key -> HTML string */
  var inflight = new Map();   /* key -> Promise */

  function ssGet(k) {
    try {
      var raw = window.sessionStorage.getItem(SS_PREFIX + k);
      if (!raw) return null;
      var o = JSON.parse(raw);
      if (!o || typeof o.h !== 'string' || (Date.now() - o.t) > CARD_TTL) {
        window.sessionStorage.removeItem(SS_PREFIX + k);
        return null;
      }
      return o.h;
    } catch (e) { return null; }
  }

  function ssSet(k, html) {
    try { window.sessionStorage.setItem(SS_PREFIX + k, JSON.stringify({ t: Date.now(), h: html })); }
    catch (e) { /* storage full / blocked: the memory cache still works */ }
  }

  /* Resolves to an HTML string ('' = no products) or null on failure (failures are never cached). */
  function fetchTarget(t) {
    if (!t || !t.handle) return Promise.resolve(null);
    var k = keyFor(t);

    if (memCache.has(k)) return Promise.resolve(memCache.get(k));
    var stored = ssGet(k);
    if (stored !== null) { memCache.set(k, stored); return Promise.resolve(stored); }
    if (inflight.has(k)) return inflight.get(k);

    var url = (t.type === 'collection')
      ? ROOT + 'collections/' + t.handle + '?view=hk-cards'
      : ROOT + 'products/' + t.handle + '?view=hk-card';

    /* A hung request must not leave the pane empty forever: abort after 10 s (-> the normal failure path). */
    var ac = window.AbortController ? new AbortController() : null;
    var abortTimer = ac ? setTimeout(function () { ac.abort(); }, 10000) : 0;
    var p = fetch(url, ac ? { credentials: 'same-origin', signal: ac.signal } : { credentials: 'same-origin' })
      .then(function (res) { if (!res.ok) throw new Error('HTTP ' + res.status); return res.text(); })
      .then(function (txt) {
        /* alternate template not uploaded yet -> Shopify answers with the full page */
        if (/<html[\s>]/i.test(txt)) throw new Error('hk template missing');
        memCache.set(k, txt);
        ssSet(k, txt);
        return txt;
      })
      .catch(function () { return null; })
      .finally(function () { clearTimeout(abortTimer); inflight.delete(k); });

    inflight.set(k, p);
    return p;
  }

  function prefetchUrl(url, pane) {
    var t = extractTarget(pickBestTargetUrl(url, pane));
    if (t) fetchTarget(t);
  }

  /* ---------- product grid (pages of 6, arrows) ---------- */
  function showMessage(container, msg) {
    if (!container) return;
    container.textContent = '';
    var div = document.createElement('div');
    div.className = 'hk-cat3__empty';
    div.style.cssText = 'padding:10px;opacity:.78;';
    div.textContent = msg;
    container.appendChild(div);
  }

  function goToPage(shell, p) {
    var scroll = shell.querySelector('[data-hk-pgrid-scroll]');
    var pages = scroll ? Array.prototype.slice.call(scroll.querySelectorAll('.hk-pgrid-page')) : [];
    shell.__hkPage = p;
    pages.forEach(function (el, i) { el.classList.toggle('is-active', i === p); });
    var prev = shell.querySelector('[data-hk-pg-prev]');
    var next = shell.querySelector('[data-hk-pg-next]');
    if (prev) prev.disabled = p <= 0;
    if (next) next.disabled = p >= pages.length - 1;
  }

  function renderCardsGrid(shell, html, i18n) {
    var scroll = shell.querySelector('[data-hk-pgrid-scroll]');
    if (!scroll) return;

    var tpl = document.createElement('template');
    tpl.innerHTML = html || '';
    var cards = Array.prototype.slice.call(tpl.content.querySelectorAll('.hk-pcard'));

    if (!cards.length) {
      showMessage(scroll, i18n.noProducts);
      goToPage(shell, 0);
      return;
    }

    scroll.textContent = '';
    for (var i = 0; i < cards.length; i += CARDS_PER_PAGE) {
      var page = document.createElement('div');
      page.className = 'hk-pgrid-page';
      cards.slice(i, i + CARDS_PER_PAGE).forEach(function (c) { page.appendChild(c); });
      scroll.appendChild(page);
    }
    goToPage(shell, 0);
  }

  /* ---------- L3 pane (built on demand) ---------- */
  function buildPane(key, title, url, i18n) {
    var pane = document.createElement('div');
    pane.className = 'hk-cat3__l3pane';
    pane.setAttribute('role', 'group');
    pane.setAttribute('aria-label', title);
    pane.dataset.hkL3Pane = key;
    pane.dataset.hkDefaultTitle = title;
    pane.dataset.hkDefaultUrl = url || '';
    pane.dataset.hkViewall = i18n.viewAll;
    pane.dataset.hkViewproduct = i18n.viewProduct;
    pane.innerHTML =
      '<div class="hk-cat3__l3head">' +
        '<a class="hk-cat3__l3title" href="' + esc(url || '#') + '">' + esc(title) + '</a>' +
        '<a class="hk-cat3__l3viewall" href="' + esc(url || '#') + '">' +
          '<span data-hk-viewall-text>' + esc(i18n.viewAll) + '</span>' +
          '<svg viewBox="0 0 24 24" focusable="false" aria-hidden="true"><path d="M9 6l6 6-6 6"></path></svg>' +
        '</a>' +
      '</div>' +
      '<div class="hk-pgrid-shell" data-hk-pgrid-shell>' +
        '<button type="button" class="hk-carousel__arrow hk-carousel__arrow--prev" data-hk-pg-prev aria-label="' + esc(i18n.prev) + '" disabled>' +
          '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M15 18l-6-6 6-6"/></svg>' +
        '</button>' +
        '<div class="hk-pgrid-scroll" data-hk-pgrid-scroll></div>' +
        '<button type="button" class="hk-carousel__arrow hk-carousel__arrow--next" data-hk-pg-next aria-label="' + esc(i18n.next) + '" disabled>' +
          '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9 18l6-6-6-6"/></svg>' +
        '</button>' +
      '</div>';
    return pane;
  }

  /* =========================================================================
     Menu controller
     root  : element that contains [data-hk-l1-btn] / [data-hk-l2-btn] / .hk-cat3__l3
     opts  : setStage(stage)  layout hook, stage is 1 | 2 | 3 | 'direct'
             canInteract()    false -> ignore all input (header while locked)
             isOpen()         false -> ignore keyboard focus navigation (header while closed)
     ========================================================================= */
  function bindMenu(root, opts) {
    opts = opts || {};
    var hooks = {
      setStage: opts.setStage || function () {},
      canInteract: opts.canInteract || function () { return true; },
      isOpen: opts.isOpen || function () { return true; }
    };
    var i18n = readI18n(root);
    var st = { stage: 1, l1: null, l2: null };
    var timers = { l1: null, l2: null };
    var suppressFocusNav = false;

    function setStage(s) { st.stage = s; hooks.setStage(s); }
    function all(sel) { return Array.prototype.slice.call(root.querySelectorAll(sel)); }

    function l3Container() { return root.querySelector('.hk-cat3__l3'); }
    function findPane(key) { return root.querySelector('[data-hk-l3-pane="' + key + '"]'); }
    function ensurePane(key, title, url) {
      var pane = findPane(key);
      if (pane) return pane;
      var host = l3Container();
      if (!host) return null;
      pane = buildPane(key, title, url, i18n);
      host.appendChild(pane);
      return pane;
    }

    function clearAll() {
      all('[data-hk-l1-btn]').forEach(function (b) { b.classList.remove('is-active'); });
      all('[data-hk-l2-pane]').forEach(function (p) { p.classList.remove('is-active'); });
      all('[data-hk-l2-btn]').forEach(function (b) { b.classList.remove('is-active'); });
      all('[data-hk-l3-pane]').forEach(function (p) { p.classList.remove('is-active'); });
      all('[data-hk-l3-preview]').forEach(function (a) { a.classList.remove('is-preview'); });
    }

    /* ----- pane content ----- */
    function applyViewAllLabel(pane, url) {
      var labelEl = pane && pane.querySelector('[data-hk-viewall-text]');
      if (!labelEl) return;
      var t = extractTarget(url);
      var txtAll = pane.dataset.hkViewall || i18n.viewAll;
      var txtProd = pane.dataset.hkViewproduct || i18n.viewProduct;
      labelEl.textContent = (t && t.type === 'product') ? txtProd : txtAll;
    }

    function setPaneHead(pane, title, url) {
      if (!pane) return;
      var t = pane.querySelector('.hk-cat3__l3title');
      var v = pane.querySelector('.hk-cat3__l3viewall');
      if (t) { t.textContent = title || t.textContent; if (url) t.href = url; }
      if (v && url) v.href = url;
      applyViewAllLabel(pane, url);
    }

    function loadCardsForPane(pane, preferredUrl) {
      if (!pane) return;
      var shell = pane.querySelector('[data-hk-pgrid-shell]');
      if (!shell) return;
      var scroll = shell.querySelector('[data-hk-pgrid-scroll]');

      var bestUrl = pickBestTargetUrl(preferredUrl, pane);
      var t = extractTarget(bestUrl);
      if (!t) { showMessage(scroll, i18n.setCollection); return; }

      applyViewAllLabel(pane, bestUrl);
      var k = keyFor(t);
      if (shell.dataset.hkFor === k && shell.dataset.hkReady === '1') return;
      shell.dataset.hkFor = k;
      shell.dataset.hkReady = '';
      fetchTarget(t).then(function (html) {
        if (shell.dataset.hkFor !== k) return;
        if (html === null) {
          /* failed: keep hkReady empty so the next hover/tap retries */
          showMessage(scroll, i18n.noProducts);
          return;
        }
        shell.dataset.hkReady = '1';
        renderCardsGrid(shell, html, i18n);
      });
    }

    function restorePaneDefault(pane) {
      if (!pane) return;
      /* the sub-links live in the L2 list, outside the pane: clear them menu-wide */
      all('[data-hk-l3-preview]').forEach(function (a) { a.classList.remove('is-preview'); });
      var defUrl = pane.dataset.hkDefaultUrl || '';
      setPaneHead(pane, pane.dataset.hkDefaultTitle || '', defUrl);
      loadCardsForPane(pane, defUrl);
    }

    function previewInPane(pane, link) {
      root.querySelectorAll('[data-hk-l3-preview]').forEach(function (a) { a.classList.remove('is-preview'); });
      link.classList.add('is-preview');
      var title = link.dataset.hkTitle || link.textContent.trim();
      var url = link.href || link.dataset.hkUrl;
      setPaneHead(pane, title, url);
      loadCardsForPane(pane, url);
    }

    /* Preview a sub-link that lives either in the L2 list or inside an L3 pane.
       Returns true when there was a pane to preview in. */
    function previewAny(link) {
      if (closest(link, '.hk-cat3__l2pane')) {
        var active = root.querySelector('.hk-cat3__l3pane.is-active');
        if (!active) return false;
        previewInPane(active, link);
        return true;
      }
      var pane = closest(link, '.hk-cat3__l3pane');
      if (!pane) return false;
      previewInPane(pane, link);
      return true;
    }

    /* ----- stages ----- */
    function activateL1(l1Key) {
      var l2Pane = root.querySelector('[data-hk-l2-pane="' + l1Key + '"]');
      var hasL2 = !!l2Pane && l2Pane.querySelectorAll('[data-hk-l2-btn]').length > 0;

      if (!hasL2) {
        /* category without sub-categories: show its own products in the big pane */
        if (st.l1 === l1Key && st.stage === 'direct') return;
        st.l1 = l1Key;
        st.l2 = null;
        all('[data-hk-l1-btn]').forEach(function (b) { b.classList.toggle('is-active', b.dataset.hkL1 === l1Key); });
        all('[data-hk-l2-pane]').forEach(function (p) { p.classList.remove('is-active'); });
        all('[data-hk-l2-btn]').forEach(function (b) { b.classList.remove('is-active'); });
        all('[data-hk-l3-pane]').forEach(function (p) { p.classList.remove('is-active'); });
        all('[data-hk-l3-preview]').forEach(function (a) { a.classList.remove('is-preview'); });
        var btn = root.querySelector('[data-hk-l1-btn][data-hk-l1="' + l1Key + '"]');
        var url = btn ? btn.getAttribute('href') : '';
        var txt = btn ? btn.querySelector('.hk-cat3__l1txt') : null;
        var title = (txt ? txt.textContent : '').trim();
        var pane = ensurePane('l1-direct-' + l1Key, title, url);
        if (pane) pane.classList.add('is-active');
        setStage('direct');
        loadCardsForPane(pane, url);
        return;
      }

      if (st.l1 === l1Key && st.stage === 2) return;
      st.l1 = l1Key;
      st.l2 = null;
      setStage(2);
      all('[data-hk-l1-btn]').forEach(function (b) { b.classList.toggle('is-active', b.dataset.hkL1 === l1Key); });
      all('[data-hk-l2-pane]').forEach(function (p) { p.classList.toggle('is-active', p.dataset.hkL2Pane === l1Key); });
      all('[data-hk-l2-btn]').forEach(function (b) { b.classList.remove('is-active'); });
      all('[data-hk-l3-pane]').forEach(function (p) { p.classList.remove('is-active'); });
      all('[data-hk-l3-preview]').forEach(function (a) { a.classList.remove('is-preview'); });
      /* warm the first two sub-categories so the next hover is instant */
      Array.prototype.slice.call(l2Pane.querySelectorAll('[data-hk-l2-btn]'), 0, 2).forEach(function (b) {
        prefetchUrl(b.href || b.dataset.hkUrl, null);
      });
    }

    function activateL2(l2Key, l1Key, l2Title, l2Url) {
      if (st.l2 === l2Key && st.stage === 3) {
        restorePaneDefault(findPane(l2Key));
        return;
      }
      st.l2 = l2Key;
      setStage(3);
      all('[data-hk-parent="' + l1Key + '"]').forEach(function (b) { b.classList.toggle('is-active', b.dataset.hkL2 === l2Key); });
      var pane = ensurePane(l2Key, l2Title, l2Url);
      all('[data-hk-l3-pane]').forEach(function (p) { p.classList.toggle('is-active', p === pane); });
      if (!pane) return;
      if (l2Title) pane.dataset.hkDefaultTitle = l2Title;
      if (l2Url) pane.dataset.hkDefaultUrl = l2Url;
      restorePaneDefault(pane);
      if (!pane.dataset.hkRestoreBound) {
        pane.dataset.hkRestoreBound = '1';
        pane.addEventListener('mouseleave', function () {
          if (isHoverDesktop() && pane.classList.contains('is-active')) restorePaneDefault(pane);
        });
      }
    }

    function activateL2FromButton(l2) {
      activateL2(
        l2.dataset.hkL2,
        l2.dataset.hkParent,
        l2.dataset.hkTitle || l2.textContent.trim(),
        l2.href || l2.dataset.hkUrl
      );
    }

    function retreat() {
      all('[data-hk-l1-btn]').forEach(function (b) { b.classList.remove('is-active'); });
      all('[data-hk-l2-btn]').forEach(function (b) { b.classList.remove('is-active'); });
      all('[data-hk-l3-pane]').forEach(function (p) { p.classList.remove('is-active'); });
      all('[data-hk-l3-preview]').forEach(function (a) { a.classList.remove('is-preview'); });
      st.l1 = null;
      st.l2 = null;
      setStage(1);
    }

    function reset() {
      clearTimeout(timers.l1);
      clearTimeout(timers.l2);
      clearAll();
      st.l1 = null;
      st.l2 = null;
      st.stage = null;
      setStage(1);
    }

    function warm() {
      var first = root.querySelector('[data-hk-l2-btn]');
      if (first) prefetchUrl(first.href || first.dataset.hkUrl, null);
    }

    /* ----- hover (with hover-intent) ----- */
    root.addEventListener('pointerover', function (e) {
      if (!isHoverDesktop() || !hooks.canInteract()) return;
      var l1 = closest(e.target, '[data-hk-l1-btn]');
      var l2 = closest(e.target, '[data-hk-l2-btn]');
      var l3 = closest(e.target, '[data-hk-l3-preview]');

      /* leaving a row before the delay is over cancels its pending activation */
      if (!l1) clearTimeout(timers.l1);
      if (!l2) clearTimeout(timers.l2);

      if (l1 && root.contains(l1)) {
        var l1Key = l1.dataset.hkL1;
        clearTimeout(timers.l1);
        timers.l1 = setTimeout(function () {
          if (isHoverDesktop() && hooks.canInteract()) activateL1(l1Key);
        }, INTENT_MS);
        return;
      }

      if (l2 && root.contains(l2)) {
        if (st.l2 === l2.dataset.hkL2 && st.stage === 3) {
          /* back on the active row: reset any sub-link preview straight away */
          clearTimeout(timers.l2);
          activateL2FromButton(l2);
          return;
        }
        clearTimeout(timers.l2);
        timers.l2 = setTimeout(function () {
          if (isHoverDesktop() && hooks.canInteract()) activateL2FromButton(l2);
        }, INTENT_MS);
        return;
      }

      if (l3 && root.contains(l3)) {
        /* reached a sub-link before its category row finished its hover-intent delay: open the row first */
        var item = closest(l3, '.hk-cat3__l2item');
        var owner = item && item.querySelector('[data-hk-l2-btn]');
        if (owner && !(st.l2 === owner.dataset.hkL2 && st.stage === 3)) {
          clearTimeout(timers.l2);
          activateL2FromButton(owner);
        }
        previewAny(l3);
      }
    });

    root.addEventListener('pointerleave', function () {
      clearTimeout(timers.l1);
      clearTimeout(timers.l2);
    });

    /* ----- keyboard: moving focus through the menu drives the same stages as hover ----- */
    root.addEventListener('focusin', function (e) {
      if (suppressFocusNav || !mqDesktop.matches || !hooks.canInteract() || !hooks.isOpen()) return;
      var el = e.target;
      if (!el || !el.closest) return;
      var kbd = true;
      try { kbd = el.matches(':focus-visible'); } catch (err) { /* old browser: treat as keyboard */ }
      if (!kbd) return;

      var l1 = el.closest('[data-hk-l1-btn]');
      if (l1 && root.contains(l1)) { activateL1(l1.dataset.hkL1); return; }

      var l2 = el.closest('[data-hk-l2-btn]');
      if (l2 && root.contains(l2)) { activateL2FromButton(l2); return; }

      var l3 = el.closest('[data-hk-l3-preview]');
      if (l3 && root.contains(l3)) previewAny(l3);
    });

    /* ----- keyboard: arrow keys between the columns (A11Y-006) -----
       Tab order puts every L1 row before the L2 lists, so Tab alone never reaches a sub-category.
       Up/Down/Home/End move inside a column, Right goes one column in, Left one column back. */
    function isShown(el) {
      return !!el && el.getClientRects().length > 0 && getComputedStyle(el).visibility !== 'hidden';
    }
    function shownIn(scope, sel) {
      return scope ? Array.prototype.slice.call(scope.querySelectorAll(sel)).filter(isShown) : [];
    }
    function stepIn(list, cur, key) {
      if (!list.length) return null;
      var i = list.indexOf(cur);
      if (key === 'Home') return list[0];
      if (key === 'End') return list[list.length - 1];
      if (key === 'ArrowDown') return list[i < 0 ? 0 : Math.min(i + 1, list.length - 1)];
      return list[i < 0 ? 0 : Math.max(i - 1, 0)];
    }
    var PANE_FOCUSABLE = 'a[href], button:not([disabled])';

    root.addEventListener('keydown', function (e) {
      if (!mqDesktop.matches || e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return;
      var key = e.key;
      var vertical = key === 'ArrowDown' || key === 'ArrowUp' || key === 'Home' || key === 'End';
      if (!vertical && key !== 'ArrowRight' && key !== 'ArrowLeft') return;
      var el = document.activeElement;
      if (!el || !root.contains(el) || !el.closest) return;

      var activeL3 = function () { return root.querySelector('.hk-cat3__l3pane.is-active'); };
      var target = null;
      var l1 = el.closest('[data-hk-l1-btn]');
      var l2Pane = el.closest('.hk-cat3__l2pane');
      var l3Pane = el.closest('.hk-cat3__l3pane');

      if (l1) {
        if (vertical) target = stepIn(shownIn(root, '[data-hk-l1-btn]'), l1, key);
        else if (key === 'ArrowRight') {
          activateL1(l1.dataset.hkL1);
          var l2s = shownIn(root.querySelector('[data-hk-l2-pane="' + l1.dataset.hkL1 + '"]'), '[data-hk-l2-btn]');
          target = l2s[0] || shownIn(activeL3(), PANE_FOCUSABLE)[0];
        }
      } else if (l2Pane) {
        if (vertical) target = stepIn(shownIn(l2Pane, '[data-hk-l2-btn], [data-hk-l3-preview]'), el, key);
        else if (key === 'ArrowLeft') target = root.querySelector('[data-hk-l1-btn][data-hk-l1="' + l2Pane.dataset.hkL2Pane + '"]');
        else {
          var own = el.closest('[data-hk-l2-btn]');
          if (own) activateL2FromButton(own);
          target = shownIn(activeL3(), PANE_FOCUSABLE)[0];
        }
      } else if (l3Pane) {
        if (vertical) target = stepIn(shownIn(l3Pane, PANE_FOCUSABLE), el, key);
        else if (key === 'ArrowLeft') {
          target = root.querySelector('.hk-cat3__l2pane.is-active [data-hk-l2-btn].is-active') ||
                   root.querySelector('[data-hk-l1-btn].is-active');
        }
      }

      if (target && target !== el) {
        e.preventDefault();
        target.focus();
      } else if (target === el) {
        e.preventDefault();
      }
    });

    /* ----- click: grid arrows + touch two-tap logic ----- */
    root.addEventListener('click', function (e) {
      var pg = closest(e.target, '[data-hk-pg-prev], [data-hk-pg-next]');
      if (pg && root.contains(pg)) {
        var shell = pg.closest('[data-hk-pgrid-shell]');
        if (shell && !pg.disabled) {
          var cur = shell.__hkPage || 0;
          goToPage(shell, pg.hasAttribute('data-hk-pg-prev') ? cur - 1 : cur + 1);
        }
        return;
      }

      if (!hooks.canInteract()) return;
      var touch = isTouchMode();

      var l1 = closest(e.target, '[data-hk-l1-btn]');
      if (l1 && root.contains(l1)) {
        /* touch: first tap opens the sub-list, second tap follows the link */
        if (touch && (st.stage === 1 || !l1.classList.contains('is-active'))) {
          e.preventDefault();
          activateL1(l1.dataset.hkL1);
        }
        return;
      }

      var l2 = closest(e.target, '[data-hk-l2-btn]');
      if (l2 && root.contains(l2)) {
        /* only a touch device at desktop width has the third column */
        if (touch && mqDesktop.matches && !l2.classList.contains('is-active')) {
          e.preventDefault();
          activateL2FromButton(l2);
        }
        return;
      }

      var l3 = closest(e.target, '[data-hk-l3-preview]');
      if (l3 && root.contains(l3)) {
        if (touch && mqDesktop.matches && !l3.classList.contains('is-preview')) {
          /* preview first, follow the link on the second tap; with no preview pane, just follow it */
          if (previewAny(l3)) e.preventDefault();
        }
      }
    });

    return {
      state: st,
      activateL1: activateL1,
      activateL2: activateL2,
      retreat: retreat,
      reset: reset,
      warm: warm,
      prefetchUrl: prefetchUrl,
      clearAll: clearAll,
      /* run fn without keyboard-focus navigation reacting (used when we move focus ourselves) */
      withoutFocusNav: function (fn) { suppressFocusNav = true; try { fn(); } finally { suppressFocusNav = false; } }
    };
  }

  window.HKCatalog = {
    bindMenu: bindMenu,
    fetchTarget: fetchTarget,
    extractTarget: extractTarget,
    prefetchUrl: prefetchUrl,
    isHoverDesktop: isHoverDesktop,
    isTouchMode: isTouchMode,
    mqDesktop: mqDesktop,
    mqCoarse: mqCoarse,
    esc: esc,
    root: ROOT,
    readI18n: readI18n
  };
})();